import { Button, Group, Stack } from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import classNames from "classnames";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "../../../../../mantine/components/IconButton/IconButton";
import { CustomValuesForTemplateSection } from "./CustomValuesForTemplateSection/CustomValuesForTemplateSection";
import type {
  FormulaData,
  FormulaTemplateEntry
} from "./ExpressionEditor.types";
import { ExpressionEditorPreview } from "./ExpressionEditorContent/ExpressionEditorPreview";
import { ExpressionTemplateSelect } from "./ExpressionTemplateSelect/ExpressionTemplateSelect";
import { useExpressionEditor } from "./hooks/useExpressionEditor";
import { VariablesAndOperatorsSection } from "./VariablesAndOperatorsSection/VariablesAndOperatorsSection";
import "./ExpressionEditor.scss";

export type ExpressionOperator = {
  value: string;
  description?: string;
};

interface ExpressionEditorProps {
  id?: string;
  name?: string;
  invalid?: boolean;
  required?: boolean;
  readOnly?: boolean;
  variables: Array<string>;
  operators: Array<ExpressionOperator>;
  otherElements: Array<ExpressionOperator>;
  formulaTemplates?: Array<FormulaTemplateEntry>;
  value?: FormulaData;
  onChange: (formulaData: FormulaData | undefined) => void;
}

function ExpressionEditor(props: ExpressionEditorProps) {
  // TODO in https://node-energy.atlassian.net/browse/WUP-8864: remove the fallback remapping after BE integrates new API
  const remappedProps = {
    ...props,
    value:
      typeof props.value === "string"
        ? { formula: (props.value as unknown as string) ?? "" }
        : props.value
  };

  // using props destructure pattern so we can pass down easily to EditorComponent later
  const {
    id,
    invalid,
    required,
    readOnly,
    variables,
    value,
    formulaTemplates
  } = remappedProps;

  const [isEditing, setIsEditing] = useState(false);

  const hasFormula = value?.formula !== undefined && value.formula !== "";

  if (readOnly) {
    return (
      <div className="ExpressionEditor" id={id}>
        <ExpressionEditorPreview
          templates={formulaTemplates}
          value={value}
          variables={variables}
        />
      </div>
    );
  }

  if (!isEditing) {
    if (hasFormula) {
      return (
        <Group className="ExpressionEditor" grow id={id}>
          <ExpressionEditorPreview
            templates={formulaTemplates}
            value={value}
            variables={variables}
            onClick={() => setIsEditing(true)}
          />
          <div>
            <IconButton
              color="primary"
              icon={IconPencil}
              size="sm"
              variant="subtle"
              onClick={() => setIsEditing(true)}
            >
              Formel editieren
            </IconButton>
          </div>
        </Group>
      );
    } else {
      return (
        <div className="ExpressionEditor" id={id}>
          <IconButton
            color={required && invalid ? "error" : "primary"}
            icon={IconPlus}
            size="sm"
            variant="subtle"
            onClick={() => setIsEditing(true)}
          >
            Formel hinzufügen
          </IconButton>
        </div>
      );
    }
  }

  // The editor component isolates all logic that actually requires the RTE editor instance.
  // This way, in preview and readOnly mode the RTE is never initialized.
  return <EditorComponent {...props} />;
}

function EditorComponent({
  id,
  invalid,
  required,
  variables,
  operators,
  otherElements,
  formulaTemplates,
  value,
  onChange
}: Omit<ExpressionEditorProps, "readOnly">) {
  const { t } = useTranslation();

  const initialValue = useRef(value);

  const { editor, editorState, insertVariable, insertText, clearExpression } =
    useExpressionEditor({
      variables,
      operators,
      otherElements,
      invalidVariablesOrFunctionsMessage: t(
        "errors.ExpressionEditor.InvalidVariablesOrFunctions"
      ),
      initialFormulaData: initialValue.current,
      onChange
    });

  const hasTemplateSelection =
    !!formulaTemplates && formulaTemplates.length > 0;

  const showMainArea =
    !hasTemplateSelection || editorState.selectedTemplateId !== null;

  const manualFormulaEntryActive = editorState.isEditable;

  return (
    <RichTextEditor
      className="ExpressionEditor"
      editor={editor}
      id={id}
      withCodeHighlightStyles={false}
      withTypographyStyles={false}
    >
      {hasTemplateSelection && (
        <ExpressionTemplateSelect
          formulaTemplates={formulaTemplates}
          initialFormulaData={initialValue.current}
          invalid={invalid}
          required={required}
        />
      )}
      {showMainArea && (
        <Group className="expression-layout-wrapper" grow>
          <div className="expression-input-wrapper">
            <RichTextEditor.Content
              className={classNames("rte-root", {
                "is-invalid": invalid
              })}
              data-testid="expression-rte"
            />
            {manualFormulaEntryActive && (
              <Button
                className="expression-clear"
                color="error"
                variant="subtle"
                onClick={clearExpression}
              >
                Formel löschen
              </Button>
            )}
          </div>
          <Stack align="start" gap="sm">
            {manualFormulaEntryActive ? (
              <VariablesAndOperatorsSection
                insertText={insertText}
                insertVariable={insertVariable}
                operators={operators}
                otherElements={otherElements}
                variables={variables}
              />
            ) : (
              <CustomValuesForTemplateSection invalid={invalid} />
            )}
          </Stack>
        </Group>
      )}
    </RichTextEditor>
  );
}

export { ExpressionEditor, ExpressionEditorProps };
